import React, { useEffect, useState } from "react";
import { fetchUserProfile, getDriverReviews } from "../utils/api";
import "../css/profileview.css";
import { API_SERVER } from "../utils/config";

const API_URL = API_SERVER; 

export default function ProfileModal({ userId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("travelo_token");
        const profileRes = await fetchUserProfile(userId, token);
        const userObj = profileRes?.user ? profileRes.user : profileRes;
        if (!mounted) return;
        setProfile(userObj || null);

        try {
          const reviewRes = await getDriverReviews(userId, token);
          if (mounted && reviewRes?.driver) setReviews(reviewRes.driver.reviews || []);
        } catch (e) { console.warn("Reviews failed"); }

      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (userId) load();
    return () => { mounted = false; };
  }, [userId]);

  if (!userId) return null;

  // ✅ Main Avatar Fix
  const avatarUrl = profile?.avatar
    ? (profile.avatar.startsWith("http") ? profile.avatar : `${API_URL}${profile.avatar}`)
    : "/default-avatar.png";

  const calcAge = (dob) => {
    if (!dob) return "";
    const age = new Date().getFullYear() - new Date(dob).getFullYear();
    return `, ${age}`;
  };

  return (
    <div className="v-modal-overlay" onClick={onClose}>
      <div className="v-modal-card animate-pop" onClick={(e) => e.stopPropagation()}>
        <button className="v-close-btn" onClick={onClose}>✕</button>

        {loading ? (
          <div className="v-loader-container">
            <div className="v-spinner"></div>
          </div>
        ) : profile ? (
          <>
            {/* Cover Banner & Overlapping Avatar */}
            <div className="v-cover-banner"></div>
            
            <div className="v-profile-header">
              <div className="v-avatar-wrapper">
                <img src={avatarUrl} alt="profile" className="v-avatar-main" />
                {profile.isEmailVerified && <div className="v-trust-badge" title="Verified Account">🛡️</div>}
              </div>

              <div className="v-user-meta">
                <h2 className="v-user-name">
                  {profile.name} <span className="v-age">{calcAge(profile.dob)}</span>
                </h2>
                <div className="v-rating-row">
                  <div className="v-star-pill">
                    <span className="v-star-icon">★</span>
                    <span className="v-rating-avg">{(Number(profile.rating) || 0).toFixed(1)}</span>
                  </div>
                  <span className="v-rating-count">{profile.totalRatings || 0} Reviews</span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="v-tabs-container">
              <div className="v-tabs-bg">
                <button className={`v-tab ${activeTab === "info" ? "active" : ""}`} onClick={() => setActiveTab("info")}>Overview</button>
                <button className={`v-tab ${activeTab === "reviews" ? "active" : ""}`} onClick={() => setActiveTab("reviews")}>Reviews</button>
              </div>
            </div>

            <div className="v-scroll-content">
              {activeTab === "info" ? (
                <div className="v-info-tab animate-fade-up">
                  
                  <h3 className="v-section-title">Trust & Safety</h3>
                  <div className="v-verification-grid">
                    <div className="v-verify-card">
                      <div className="v-verify-icon-box blue">📧</div>
                      <div className="v-verify-info">
                        <span className="v-verify-label">Email Status</span>
                        {profile.isEmailVerified ? (
                          <span className="v-status-verified"><span className="v-dot green"></span> Verified</span>
                        ) : (
                          <span className="v-status-pending"><span className="v-dot red"></span> Pending</span>
                        )}
                      </div>
                    </div>

                    <div className="v-verify-card">
                      <div className="v-verify-icon-box purple">📱</div>
                      <div className="v-verify-info">
                        <span className="v-verify-label">Mobile Number</span>
                        <span className="v-status-verified"><span className="v-dot green"></span> Verified</span>
                      </div>
                    </div>
                  </div>

                  <h3 className="v-section-title">Traveler Details</h3>
                  <div className="v-details-list">
                    <div className="v-detail-row">
                      <div className="v-detail-left"><span className="v-detail-icon">🚻</span> Gender</div>
                      <div className="v-detail-right">{profile.gender || "—"}</div>
                    </div>
                    <div className="v-detail-row">
                      <div className="v-detail-left"><span className="v-detail-icon">🏠</span> Location</div>
                      <div className="v-detail-right">{profile.address || "—"}</div>
                    </div>
                    
                    <div className="v-vehicle-premium-card">
                      <div className="v-vehicle-icon">🚗</div>
                      <div className="v-vehicle-data">
                        <span className="v-vehicle-label">Registered Vehicle</span>
                        <span className="v-vehicle-name">{profile.vehicle || "No vehicle added"}</span>
                      </div>
                    </div>
                  </div>

                </div>
              ) : (
                <div className="v-reviews-tab animate-fade-up">
                  {reviews.length > 0 ? (
                    reviews.map((r, i) => {
                      
                      // ✅ FIXED: Safely handle reviewer avatar URLs
                      const reviewerAvatar = r.from?.avatar 
                        ? (r.from.avatar.startsWith('http') ? r.from.avatar : `${API_URL}${r.from.avatar}`) 
                        : "/default-avatar.png";

                      return (
                        <div key={i} className="v-review-card">
                          <div className="v-review-top">
                            <img src={reviewerAvatar} className="v-rev-img" alt="reviewer" />
                            <div className="v-rev-meta">
                              <span className="v-rev-name">{r.from?.name || "Passenger"}</span>
                              <span className="v-rev-date">{new Date(r.date || r.createdAt).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</span>
                            </div>
                            <div className="v-rev-stars">
                              {"★".repeat(r.rating || 0)}<span className="v-star-empty">{"★".repeat(5 - (r.rating || 0))}</span>
                            </div>
                          </div>
                          <p className="v-rev-text">{r.review }</p>
                        </div>
                      );
                    })
                  ) : (
                    <div className="v-empty-state">
                      <span className="v-empty-icon">⭐</span>
                      <p>No reviews yet for this traveler.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}